// src/services/visitorConversationServices.js

import axios from "axios";

const apiUrl = `${process.env.REACT_APP_API_URL}/api/visitor-conversations`;

function getAuthHeaders() {
  const token = localStorage.getItem("token");

  if (!token) {
    return {};
  }

  return {
    Authorization: `Bearer ${token}`,
  };
}

function handleServiceError(message, error) {
  console.error(message, error);

  if (error?.response?.data) {
    console.error("Réponse API :", error.response.data);
  }

  throw error;
}

/* -------------------------------------------------------------------------- */
/* ROUTES PUBLIQUES                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Ouvre ou récupère une conversation pour une session visiteur.
 *
 * @param {Object} conversationData
 * @returns {Promise<Object>}
 */
export const openVisitorConversation = async (conversationData) => {
  try {
    const response = await axios.post(
      `${apiUrl}/open`,
      conversationData,
    );

    return response.data;
  } catch (error) {
    return handleServiceError(
      "Erreur lors de l'ouverture de la conversation :",
      error,
    );
  }
};

/**
 * Envoie un message écrit par un visiteur.
 *
 * @param {number|string} conversationId
 * @param {Object} messageData
 * @returns {Promise<Object>}
 */
export const sendVisitorMessage = async (
  conversationId,
  messageData,
) => {
  try {
    if (!conversationId) {
      throw new Error("L'identifiant de la conversation est requis.");
    }

    const response = await axios.post(
      `${apiUrl}/${conversationId}/messages/visitor`,
      messageData,
    );

    return response.data;
  } catch (error) {
    return handleServiceError(
      "Erreur lors de l'envoi du message visiteur :",
      error,
    );
  }
};

/**
 * Marque les messages administrateur comme lus par le visiteur.
 *
 * @param {number|string} conversationId
 * @param {string} sessionToken
 * @returns {Promise<Object>}
 */
export const markConversationAsReadByVisitor = async (
  conversationId,
  sessionToken,
) => {
  try {
    if (!conversationId) {
      throw new Error("L'identifiant de la conversation est requis.");
    }

    const response = await axios.patch(
      `${apiUrl}/${conversationId}/read-visitor`,
      {
        session_token: sessionToken,
      },
    );

    return response.data;
  } catch (error) {
    return handleServiceError(
      "Erreur lors du marquage des messages comme lus par le visiteur :",
      error,
    );
  }
};

/* -------------------------------------------------------------------------- */
/* ROUTES ADMINISTRATEUR                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Récupère la liste paginée des conversations.
 *
 * Filtres disponibles :
 * - page
 * - limit
 * - status
 * - unread
 *
 * @param {Object} params
 * @returns {Promise<Object>}
 */
export const getVisitorConversations = async (params = {}) => {
  try {
    const response = await axios.get(apiUrl, {
      headers: getAuthHeaders(),
      params,
    });

    return response.data;
  } catch (error) {
    return handleServiceError(
      "Erreur lors de la récupération des conversations :",
      error,
    );
  }
};

/**
 * Récupère une conversation avec les messages et le parcours visiteur.
 *
 * @param {number|string} conversationId
 * @returns {Promise<Object>}
 */
export const getVisitorConversationById = async (
  conversationId,
) => {
  try {
    if (!conversationId) {
      throw new Error("L'identifiant de la conversation est requis.");
    }

    const response = await axios.get(
      `${apiUrl}/${conversationId}`,
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  } catch (error) {
    return handleServiceError(
      "Erreur lors de la récupération de la conversation :",
      error,
    );
  }
};

/**
 * Envoie un message administrateur.
 *
 * @param {number|string} conversationId
 * @param {string|Object} message
 * @returns {Promise<Object>}
 */
export const sendAdminConversationMessage = async (
  conversationId,
  message,
) => {
  try {
    if (!conversationId) {
      throw new Error("L'identifiant de la conversation est requis.");
    }

    const payload =
      typeof message === "string"
        ? { message }
        : message;

    const response = await axios.post(
      `${apiUrl}/${conversationId}/messages/admin`,
      payload,
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  } catch (error) {
    return handleServiceError(
      "Erreur lors de l'envoi du message administrateur :",
      error,
    );
  }
};

/**
 * Modifie une conversation.
 *
 * Champs modifiables :
 * - status
 * - assigned_user_id
 * - visitor_name
 * - visitor_email
 * - visitor_phone
 * - organization_name
 * - subject
 *
 * @param {number|string} conversationId
 * @param {Object} conversationData
 * @returns {Promise<Object>}
 */
export const updateVisitorConversation = async (
  conversationId,
  conversationData,
) => {
  try {
    if (!conversationId) {
      throw new Error("L'identifiant de la conversation est requis.");
    }

    const response = await axios.patch(
      `${apiUrl}/${conversationId}`,
      conversationData,
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  } catch (error) {
    return handleServiceError(
      "Erreur lors de la modification de la conversation :",
      error,
    );
  }
};

/**
 * Marque les messages visiteur comme lus par l'administrateur.
 *
 * @param {number|string} conversationId
 * @returns {Promise<Object>}
 */
export const markConversationAsReadByAdmin = async (
  conversationId,
) => {
  try {
    if (!conversationId) {
      throw new Error("L'identifiant de la conversation est requis.");
    }

    const response = await axios.patch(
      `${apiUrl}/${conversationId}/read-admin`,
      {},
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  } catch (error) {
    return handleServiceError(
      "Erreur lors du marquage des messages comme lus :",
      error,
    );
  }
};

/**
 * Ferme une conversation.
 *
 * @param {number|string} conversationId
 * @returns {Promise<Object>}
 */
export const closeVisitorConversation = async (
  conversationId,
) => {
  return updateVisitorConversation(conversationId, {
    status: "closed",
  });
};

/**
 * Rouvre une conversation.
 *
 * @param {number|string} conversationId
 * @returns {Promise<Object>}
 */
export const reopenVisitorConversation = async (
  conversationId,
) => {
  return updateVisitorConversation(conversationId, {
    status: "open",
  });
};

/**
 * Récupère une conversation depuis le widget visiteur.
 *
 * Le session_token permet de vérifier que la conversation
 * appartient bien au navigateur demandeur.
 */
export const getPublicVisitorConversation = async (
  conversationId,
  sessionToken,
) => {
  try {
    if (!conversationId) {
      throw new Error(
        "L'identifiant de la conversation est requis.",
      );
    }

    if (!sessionToken) {
      throw new Error(
        "Le token de session visiteur est requis.",
      );
    }

    const response = await axios.post(
      `${apiUrl}/${conversationId}/public`,
      {
        session_token: sessionToken,
      },
    );

    return response.data;
  } catch (error) {
    return handleServiceError(
      "Erreur lors de la récupération de la conversation visiteur :",
      error,
    );
  }
};